import * as Sentry from '@sentry/react';
import { Constraints, ConstraintType } from '@superfunnel/types';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ConstraintsForm from '../components/Constraints/ConstraintsForm';
import { useDynamicPageContext } from '../contexts/DynamicPageContext';
import { UserContext } from '../contexts/UserContext';
import { updateVariant } from '../services/DynamicPageVariantsService';

interface Props {
  variantId?: string;
  updatableConstraints?: ConstraintType[];
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
}

export default function EditVariantConstraintsModal({
  variantId,
  updatableConstraints,
  open,
  onClose,
  title,
  subtitle,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage, variants } = useDynamicPageContext();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();

  const [constraints, setConstraints] = useState<Constraints>();

  const variant = useMemo(
    () => (variantId ? variants?.find((v) => v.id === variantId) : undefined),
    [variantId, variants],
  );

  useEffect(() => {
    setConstraints(variant?.data().constraints || {});
  }, [variant]);

  useEffect(() => {
    if (open) return;

    setSaving(false);
    setError(undefined);
  }, [open]);

  const saveConstraints = useCallback(async () => {
    if (saving) return;
    if (!variant) return;
    if (!selectedAccount || !selectedDynamicPage) return;

    if (!updatableConstraints || updatableConstraints.includes('schedules')) {
      if (
        constraints?.schedules?.find((s) => {
          // filter out schedules that have no at least one of: startDate, endDate, daysOfWeek, hoursOfDay
          return (
            !s.startDate &&
            !s.endDate &&
            !s.daysOfWeek?.length &&
            !s.hoursOfDay?.length
          );
        })
      ) {
        setError(
          new Error(
            'Schedules must have at least one of: startDate, endDate, daysOfWeek, hoursOfDay',
          ),
        );
        return;
      }
    }

    setSaving(true);

    try {
      const update = {
        ...(!updatableConstraints || updatableConstraints.includes('keywords')
          ? { 'constraints.keywords': constraints?.keywords ?? [] }
          : {}),
        ...(!updatableConstraints || updatableConstraints.includes('devices')
          ? { 'constraints.devices': constraints?.devices ?? [] }
          : {}),
        ...(!updatableConstraints || updatableConstraints.includes('adGroups')
          ? { 'constraints.adGroups': constraints?.adGroups ?? [] }
          : {}),
        ...(!updatableConstraints || updatableConstraints.includes('ads')
          ? { 'constraints.ads': constraints?.ads ?? [] }
          : {}),
        ...(!updatableConstraints || updatableConstraints.includes('campaigns')
          ? { 'constraints.campaigns': constraints?.campaigns ?? [] }
          : {}),
        ...(!updatableConstraints || updatableConstraints.includes('schedules')
          ? {
              'constraints.schedules': constraints?.schedules ?? [],
            }
          : {}),
        ...(!updatableConstraints || updatableConstraints.includes('countries')
          ? {
              'constraints.countries': constraints?.countries ?? [],
            }
          : {}),
        ...(!updatableConstraints || updatableConstraints.includes('regions')
          ? {
              'constraints.regions': constraints?.regions ?? [],
            }
          : {}),
        ...(!updatableConstraints || updatableConstraints.includes('cities')
          ? {
              'constraints.cities': constraints?.cities ?? [],
            }
          : {}),
      };
      await updateVariant(
        selectedAccount.id,
        selectedDynamicPage.id,
        variant.id,
        update,
      );
      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setSaving(false);
  }, [
    saving,
    variant,
    selectedAccount,
    selectedDynamicPage,
    updatableConstraints,
    constraints?.schedules,
    constraints?.keywords,
    constraints?.devices,
    constraints?.adGroups,
    constraints?.ads,
    constraints?.campaigns,
    constraints?.countries,
    constraints?.regions,
    constraints?.cities,
    onClose,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: 'Update',
        type: 'primary',
        loading: saving,
        onClick: () => saveConstraints(),
      },
    ],
    [saving, onClose, saveConstraints],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      width="lg"
      title={title || 'Smart Traffic Rules Configuration'}
    >
      <p className="mt-0 text-sm text-gray-500">
        {subtitle ||
          `Define and manage the rules for directing traffic to this variant. Use
        smart constraints like devices, keywords, ad groups, ads and audience
        preferences to ensure your variant is shown to the right users.`}
      </p>
      <div className="mt-4 flex flex-col gap-4">
        <ConstraintsForm
          visibleConstraints={updatableConstraints}
          constraints={constraints || {}}
          onChange={(newConstraints) => {
            setConstraints(newConstraints);
            setError(undefined);
          }}
        />
      </div>
      {error ? (
        <div className="mt-5 w-full text-sm text-red-600">
          {error.message || 'Failed to create workspace'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
